import type React from 'react';
import {useState} from 'react';
import {Box, Button, Divider, Stack, Typography} from '@mui/material';
import {ExpandLess, ExpandMore} from '@mui/icons-material';
import {SegmentedControl} from '~/components/segmented-control/SegmentedControl';
import type {RecordActions} from '../../hooks/useRecordActions';
import type {CategoryRecord} from '../../types/categoryRecordTypes';
import {ProfileSection} from '../components/ProfileSection';
import {NoteComposer} from './NoteComposer';
import {NoteView} from './NoteView';
import {OVERVIEW_HELP} from '../../domain/overviewHelp';

const FILTERS = ['All', 'Open', 'Resolved'] as const;

type Props = {record: CategoryRecord; actions: RecordActions};

/**
 * Notes (spec 4.4): the category's shared notes thread. Notes carry a title, topic, status, reactions and the Internal
 * only flag; replies (one level) are collapsed until opened.
 */
export const NotesSection: React.FC<Props> = ({record, actions}) => {
    const [filter, setFilter] = useState(0);
    const [replyTo, setReplyTo] = useState<number | null>(null);
    const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
    const {canEdit, options, notes} = record;
    const open = notes.filter(p => p.status === 'open').length;
    const shown = notes.filter(p => FILTERS[filter] === 'All' || p.status === FILTERS[filter].toLowerCase());

    const toggle = (id: number, value?: boolean) => setExpanded(prev => {
        const next = new Set(prev);
        if (value ?? !next.has(id)) next.add(id);
        else next.delete(id);
        return next;
    });
    const reply = (id: number) => {
        setReplyTo(replyTo === id ? null : id);
        toggle(id, true);
    };

    return (
        <ProfileSection title={`Notes (${notes.length})`} help={OVERVIEW_HELP.notes} subtitle={open ? `${open} open` : undefined}>
            <Stack gap={2}>
                {canEdit && (
                    <NoteComposer topics={options.topics} placeholder="Add a note on this category" submitLabel="Post"
                                  onSubmit={input => actions.postNote(input)}/>
                )}
                {notes.length > 0 && (
                    <SegmentedControl labels={[...FILTERS]} value={filter} onChange={setFilter} sx={{alignSelf: 'flex-start'}}/>
                )}
                {shown.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        {notes.length === 0 ? 'No notes yet.' : `No ${FILTERS[filter].toLowerCase()} notes.`}
                    </Typography>
                ) : (
                    <Stack divider={<Divider flexItem/>} gap={2}>
                        {shown.map(note => {
                            const n = note.replies.length;
                            const isOpen = expanded.has(note.id);
                            return (
                                <Stack key={note.id} gap={1}>
                                    <NoteView note={note} actions={actions} canEdit={canEdit} reactions={options.reactions}
                                              onReply={() => reply(note.id)}/>
                                    {n > 0 && (
                                        <Button size="small" onClick={() => toggle(note.id)} aria-expanded={isOpen}
                                                startIcon={isOpen ? <ExpandLess/> : <ExpandMore/>}
                                                sx={{alignSelf: 'flex-start', ml: 4.5}}>
                                            {isOpen ? 'Hide replies' : `Show ${n} ${n === 1 ? 'reply' : 'replies'}`}
                                        </Button>
                                    )}
                                    {isOpen && (n > 0 || replyTo === note.id) && (
                                        <Stack gap={1.5} sx={{pl: 5}}>
                                            {note.replies.map(r => (
                                                <NoteView key={r.id} note={r} actions={actions} canEdit={canEdit}
                                                          reactions={options.reactions}/>
                                            ))}
                                            {replyTo === note.id && (
                                                <Box>
                                                    <NoteComposer autoFocus placeholder={`Reply to ${note.author.name}`}
                                                                  submitLabel="Reply"
                                                                  onSubmit={input => actions.postNote(input, note.id)}
                                                                  onCancel={() => setReplyTo(null)}/>
                                                </Box>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </Stack>
        </ProfileSection>
    );
};
